import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  Building2,
  Check,
  Copy,
  FileUp,
  ListChecks,
  Plus,
  Sparkles,
  Upload,
  Users as UsersIcon,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { AdminGuard } from '@/components/AdminGuard';
import { PageError, PageLoading, Spinner } from '@/components/Spinner';
import {
  api,
  type Catalogue,
  type CodeName,
  type Language,
  type PersonaUploadResult,
  type PersonaUploadSpec,
} from '@/lib/api';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/onboarding')({
  component: OnboardingPage,
});

function OnboardingPage() {
  return (
    <AdminGuard>
      <OnboardingBody />
    </AdminGuard>
  );
}

const REGISTER_TYPES = ['rulebook', 'conversation_design', 'verbalism'] as const;

function OnboardingBody() {
  const catQ = useQuery({ queryKey: ['catalogue'], queryFn: api.getCatalogue });

  if (catQ.isLoading) return <PageLoading />;
  if (catQ.error || !catQ.data) return <PageError message={String(catQ.error)} />;
  const cat = catQ.data;

  return (
    <div className="space-y-10">
      <div>
        <h1>Onboard a bank</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Add a new bank or financial player, then upload its rule registers and personas.
          Everything here is additive — the existing cells are untouched.
        </p>
      </div>

      <AddBankSection cat={cat} />
      <RegisterUploadSection cat={cat} />
      <PersonaUploadSection cat={cat} />
      <SharedChecklistSection cat={cat} />
    </div>
  );
}

const inputCls =
  'mt-1 h-9 w-full rounded-md border border-input bg-card px-3 text-sm shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const labelCls = 'text-xs font-medium uppercase tracking-wide text-muted-foreground';
const cardCls = 'rounded-lg border border-border bg-card p-4 shadow-sm';
const btnCls =
  'inline-flex h-9 items-center justify-center gap-1.5 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90 disabled:opacity-60';

// ── Section 1: Add a bank ───────────────────────────────────────────

function AddBankSection({ cat }: { cat: Catalogue }) {
  const qc = useQueryClient();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [products, setProducts] = useState<CodeName[]>([]);
  const [buckets, setBuckets] = useState<CodeName[]>([]);
  const [error, setError] = useState<string | null>(null);

  const addMut = useMutation({
    mutationFn: () =>
      api.addBank({
        code: code.trim(),
        name: name.trim(),
        products: products.map((p) => ({ code: p.code, name: p.name })),
        buckets: buckets.map((b) => ({ code: b.code, name: b.name })),
      }),
    onSuccess: () => {
      toast.success(`Onboarded ${code.trim()} — ${products.length * buckets.length} cell(s) created`);
      setCode('');
      setName('');
      setProducts([]);
      setBuckets([]);
      setError(null);
      // Refresh the catalogue everywhere (this page + every picker).
      qc.invalidateQueries({ queryKey: ['catalogue'] });
      qc.invalidateQueries({ queryKey: ['cells'] });
    },
    onError: (e) => {
      const msg = String(e);
      setError(msg.includes('409') ? 'That bank code already exists.' : msg);
    },
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!/^[A-Za-z0-9]{1,16}$/.test(code.trim())) {
      setError('Bank code must be 1–16 letters/digits, no spaces or underscores.');
      return;
    }
    if (!name.trim()) {
      setError('Display name is required.');
      return;
    }
    if (products.length === 0 || buckets.length === 0) {
      setError('Pick at least one product and one bucket.');
      return;
    }
    setError(null);
    addMut.mutate();
  }

  return (
    <section>
      <h2 className="flex items-center gap-2 text-base font-medium text-muted-foreground">
        <Building2 className="size-4" /> 1 · Add a bank
      </h2>
      <p className="mt-1 text-xs text-muted-foreground">
        Creates one cell per product × bucket combination you select. New product/bucket codes
        are added to the catalogue.
      </p>
      <form onSubmit={onSubmit} className={cn('mt-3 space-y-4', cardCls)}>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className={labelCls}>Bank code</label>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className={inputCls}
              placeholder="FAB"
            />
          </div>
          <div>
            <label className={labelCls}>Display name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputCls}
              placeholder="First Abu Dhabi Bank"
            />
          </div>
        </div>

        <VocabPicker
          label="Products"
          existing={cat.products}
          selected={products}
          onChange={setProducts}
          placeholderCode="AUTO"
          placeholderName="Auto Loan"
        />
        <VocabPicker
          label="Buckets"
          existing={cat.buckets}
          selected={buckets}
          onChange={setBuckets}
          placeholderCode="90DPD"
          placeholderName="90 Days Past Due"
        />

        {products.length > 0 && buckets.length > 0 && (
          <p className="text-xs text-muted-foreground">
            Will create:{' '}
            <span className="font-mono text-foreground">
              {products.flatMap((p) => buckets.map((b) => `${code || 'BANK'}_${p.code}_${b.code}`)).join(', ')}
            </span>
          </p>
        )}

        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-2.5 text-sm text-destructive">
            {error}
          </div>
        )}

        <button type="submit" disabled={addMut.isPending} className={btnCls}>
          {addMut.isPending ? <Spinner className="size-4 text-primary-foreground" /> : <Plus className="size-4" />}
          Onboard bank
        </button>
      </form>
    </section>
  );
}

function VocabPicker({
  label,
  existing,
  selected,
  onChange,
  placeholderCode,
  placeholderName,
}: {
  label: string;
  existing: CodeName[];
  selected: CodeName[];
  onChange: (next: CodeName[]) => void;
  placeholderCode: string;
  placeholderName: string;
}) {
  const [newCode, setNewCode] = useState('');
  const [newName, setNewName] = useState('');
  const selectedCodes = useMemo(() => new Set(selected.map((s) => s.code)), [selected]);

  function toggle(item: CodeName) {
    if (selectedCodes.has(item.code)) {
      onChange(selected.filter((s) => s.code !== item.code));
    } else {
      onChange([...selected, item]);
    }
  }

  function addCustom() {
    const c = newCode.trim();
    if (!/^[A-Za-z0-9]{1,16}$/.test(c)) {
      toast.error('Code must be 1–16 letters/digits, no spaces or underscores.');
      return;
    }
    if (selectedCodes.has(c) || existing.some((e) => e.code === c)) {
      toast.error(`${c} already exists — tick it above instead.`);
      return;
    }
    onChange([...selected, { code: c, name: newName.trim() || c }]);
    setNewCode('');
    setNewName('');
  }

  // Custom (not-in-existing) selected entries to render as removable chips.
  const customSelected = selected.filter((s) => !existing.some((e) => e.code === s.code));

  return (
    <div>
      <label className={labelCls}>{label}</label>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {existing.map((item) => (
          <button
            key={item.code}
            type="button"
            onClick={() => toggle(item)}
            title={item.name}
            className={cn(
              'rounded-full border px-2.5 py-1 text-xs font-medium',
              selectedCodes.has(item.code)
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border bg-card text-muted-foreground hover:border-primary/40',
            )}
          >
            {item.code}
          </button>
        ))}
        {customSelected.map((item) => (
          <span
            key={item.code}
            className="inline-flex items-center gap-1 rounded-full border border-primary bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary"
          >
            {item.code}
            <button type="button" onClick={() => toggle(item)} title="remove">
              <X className="size-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input
          value={newCode}
          onChange={(e) => setNewCode(e.target.value)}
          className="h-8 w-28 rounded-md border border-input bg-card px-2 text-xs shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          placeholder={`new code (${placeholderCode})`}
        />
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          className="h-8 w-44 rounded-md border border-input bg-card px-2 text-xs shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          placeholder={`name (${placeholderName})`}
        />
        <button
          type="button"
          onClick={addCustom}
          className="inline-flex h-8 items-center gap-1 rounded-md border border-border bg-card px-2 text-xs font-medium text-muted-foreground hover:border-primary/40 hover:text-foreground"
        >
          <Plus className="size-3" /> add new {label.toLowerCase().replace(/s$/, '')}
        </button>
      </div>
    </div>
  );
}

// ── Section 2: Upload registers ─────────────────────────────────────

function RegisterUploadSection({ cat }: { cat: Catalogue }) {
  const cells = cat.cells.filter((c) => c.enabled);
  const [cell, setCell] = useState(cells[0]?.slug ?? '');
  const [type, setType] = useState<string>(REGISTER_TYPES[0]);
  const [bank, setBank] = useState(cat.banks[0]?.code ?? '');

  const regRef = useRef<HTMLInputElement>(null);
  const vardictRef = useRef<HTMLInputElement>(null);

  const regMut = useMutation({
    mutationFn: (file: File) => api.uploadCellRegister(cell, type, file),
    onSuccess: (r) => toast.success(`Uploaded ${type} for ${cell} (${r.body_length} chars)`),
    onError: (e: Error) => toast.error(e.message),
  });
  const vardictMut = useMutation({
    mutationFn: (file: File) => api.uploadVardict(bank, file),
    onSuccess: (r) => toast.success(`Uploaded variable dictionary for ${bank} (${r.body_length} chars)`),
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <section>
      <h2 className="flex items-center gap-2 text-base font-medium text-muted-foreground">
        <FileUp className="size-4" /> 2 · Upload registers
      </h2>
      <p className="mt-1 text-xs text-muted-foreground">
        Upload the compressed register documents (.docx → auto-converted, or .md as-is). Re-uploads
        archive the previous version.
      </p>

      <div className="mt-3 grid gap-4 lg:grid-cols-2">
        <div className={cardCls}>
          <div className="text-sm font-medium">Per-cell register</div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Cell</label>
              <select value={cell} onChange={(e) => setCell(e.target.value)} className={inputCls}>
                {cells.map((c) => (
                  <option key={c.slug} value={c.slug}>
                    {c.bank} · {c.product} · {c.bucket}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Register type</label>
              <select value={type} onChange={(e) => setType(e.target.value)} className={inputCls}>
                {REGISTER_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <input
            ref={regRef}
            type="file"
            accept=".docx,.md,.markdown,.txt"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) regMut.mutate(f);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            onClick={() => regRef.current?.click()}
            disabled={regMut.isPending || !cell}
            className={cn(btnCls, 'mt-3')}
          >
            {regMut.isPending ? <Spinner className="size-4 text-primary-foreground" /> : <Upload className="size-4" />}
            Choose file & upload
          </button>
        </div>

        <div className={cardCls}>
          <div className="text-sm font-medium">Per-bank variable dictionary</div>
          <div className="mt-3">
            <label className={labelCls}>Bank</label>
            <select value={bank} onChange={(e) => setBank(e.target.value)} className={inputCls}>
              {cat.banks.map((b) => (
                <option key={b.code} value={b.code}>
                  {b.code} — {b.name}
                </option>
              ))}
            </select>
          </div>
          <input
            ref={vardictRef}
            type="file"
            accept=".docx,.md,.markdown,.txt"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) vardictMut.mutate(f);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            onClick={() => vardictRef.current?.click()}
            disabled={vardictMut.isPending || !bank}
            className={cn(btnCls, 'mt-3')}
          >
            {vardictMut.isPending ? <Spinner className="size-4 text-primary-foreground" /> : <Upload className="size-4" />}
            Choose file & upload
          </button>
        </div>
      </div>
    </section>
  );
}

// ── Section 3: Upload personas ──────────────────────────────────────

function PersonaUploadSection({ cat }: { cat: Catalogue }) {
  const cells = cat.cells.filter((c) => c.enabled);
  const [cell, setCell] = useState(cells[0]?.slug ?? '');
  const [lang, setLang] = useState<Language>('EN');
  const [result, setResult] = useState<PersonaUploadResult | null>(null);
  // Whole-file failures (bad JSON / wrong top-level shape) come back as
  // a thrown error, not a 200 report — capture the message so we can
  // show the same fix-it panel for them too.
  const [fileError, setFileError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Canonical format spec, fetched once — feeds the copyable "tell an
  // AI how to fix it" prompt so the spec never drifts from the backend.
  const specQ = useQuery({
    queryKey: ['persona-upload-spec'],
    queryFn: api.getPersonaUploadSpec,
    staleTime: Infinity,
  });

  const upMut = useMutation({
    mutationFn: (file: File) => api.uploadPersonas(cell, lang, file),
    onSuccess: (r) => {
      setFileError(null);
      setResult(r);
      if (r.written) toast.success(`Added ${r.added.length} persona(s) to ${cell}/${lang}`);
      else if (r.invalid.length > 0) toast.error(`${r.invalid.length} persona(s) couldn't be read — see how to fix below.`);
      else toast.info('Nothing added — all ids already existed.');
    },
    onError: (e: Error) => {
      setResult(null);
      // apiFetch throws "<status> <text>: <detail>" — show just the detail.
      setFileError(e.message.replace(/^\d{3}[^:]*:\s*/, '') || e.message);
      toast.error('Upload failed — see how to fix below.');
    },
  });

  // Collect issues from EITHER path into one shape for the fix-it panel.
  const issues: { label: string; error: string }[] = [];
  if (fileError) {
    issues.push({ label: 'whole file', error: fileError });
  } else if (result) {
    for (const inv of result.invalid) {
      issues.push({
        label: inv.id ? `persona "${inv.id}"` : `persona #${inv.index + 1}`,
        error: inv.error,
      });
    }
  }

  return (
    <section>
      <h2 className="flex items-center gap-2 text-base font-medium text-muted-foreground">
        <UsersIcon className="size-4" /> 3 · Upload personas (JSON)
      </h2>
      <p className="mt-1 text-xs text-muted-foreground">
        Upload a personas.json (envelope or bare array). Only personas whose id isn't already
        present are added; existing ids are kept untouched.
      </p>

      <div className={cn('mt-3', cardCls)}>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className={labelCls}>Cell</label>
            <select value={cell} onChange={(e) => setCell(e.target.value)} className={inputCls}>
              {cells.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.bank} · {c.product} · {c.bucket}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Language</label>
            <select value={lang} onChange={(e) => setLang(e.target.value as Language)} className={inputCls}>
              <option value="EN">EN</option>
              <option value="AR">AR</option>
            </select>
          </div>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept=".json"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) upMut.mutate(f);
            e.target.value = '';
          }}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={upMut.isPending || !cell}
          className={cn(btnCls, 'mt-3')}
        >
          {upMut.isPending ? <Spinner className="size-4 text-primary-foreground" /> : <Upload className="size-4" />}
          Choose personas.json & upload
        </button>

        {result && (
          <div className="mt-4 space-y-1 rounded-md border border-border bg-muted/20 p-3 text-xs">
            <div>
              <span className="font-medium text-success">Added:</span>{' '}
              {result.added.length ? result.added.join(', ') : '—'}
            </div>
            <div>
              <span className="font-medium text-status-warning-text">Skipped (already present):</span>{' '}
              {result.skipped_existing.length ? result.skipped_existing.join(', ') : '—'}
            </div>
            <div className="pt-1 text-muted-foreground">
              Total personas now in {cell}/{lang}: {result.persona_count}
            </div>
          </div>
        )}

        {issues.length > 0 && (
          <PersonaFixItPanel
            cell={cell}
            language={lang}
            issues={issues}
            spec={specQ.data ?? null}
          />
        )}
      </div>
    </section>
  );
}

// Renders the problems found in a persona upload plus a copyable prompt
// the operator can paste into any AI to regenerate a correct file.
function PersonaFixItPanel({
  cell,
  language,
  issues,
  spec,
}: {
  cell: string;
  language: Language;
  issues: { label: string; error: string }[];
  spec: PersonaUploadSpec | null;
}) {
  const [copied, setCopied] = useState(false);
  const prompt = buildPersonaFixPrompt(cell, language, issues, spec);

  async function copy() {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Copy failed — select the text and copy manually.');
    }
  }

  return (
    <div className="mt-4 space-y-3 rounded-md border border-destructive/40 bg-destructive/5 p-3">
      <div className="text-xs">
        <span className="font-semibold text-destructive">
          {issues.length} problem{issues.length === 1 ? '' : 's'} — nothing was changed for{' '}
          {issues.length === 1 ? 'it' : 'these'}:
        </span>
        <ul className="mt-1.5 list-disc space-y-0.5 pl-5 text-muted-foreground">
          {issues.map((it, i) => (
            <li key={i}>
              <span className="font-medium text-foreground">{it.label}:</span> {it.error}
            </li>
          ))}
        </ul>
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className="text-xs font-medium text-muted-foreground">
            Copy this and paste it to any AI to regenerate a correct file:
          </span>
          <button
            type="button"
            onClick={copy}
            className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border bg-card px-2.5 text-xs font-medium hover:bg-accent"
          >
            {copied ? <Check className="size-3.5 text-success" /> : <Copy className="size-3.5" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
        <textarea
          readOnly
          value={prompt}
          onFocus={(e) => e.currentTarget.select()}
          rows={14}
          className="w-full resize-y rounded-md border border-input bg-card p-3 font-mono text-[11px] leading-relaxed text-foreground shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>
    </div>
  );
}

function buildPersonaFixPrompt(
  cell: string,
  language: Language,
  issues: { label: string; error: string }[],
  spec: PersonaUploadSpec | null,
): string {
  const lines: string[] = [];
  lines.push(
    'I need test personas for a collections-call testing tool, as a single JSON file.',
    '',
  );
  if (spec) {
    lines.push('OUTPUT FORMAT — a JSON object exactly like this (a bare JSON array of persona objects is also accepted):');
    lines.push(JSON.stringify(spec.envelope_example, null, 2));
    lines.push('');
    lines.push('EACH PERSONA — required fields:');
    for (const f of spec.required_fields) lines.push(`  - ${f.name}: ${f.note}`);
    lines.push('Optional fields:');
    for (const f of spec.optional_fields) lines.push(`  - ${f.name}: ${f.note}`);
    lines.push('');
    if (spec.notes?.length) {
      lines.push('RULES:');
      for (const n of spec.notes) lines.push(`  - ${n}`);
      lines.push('');
    }
  } else {
    lines.push('(Format spec unavailable — fetch /api/personas/upload-spec for the exact fields.)', '');
  }
  lines.push('PROBLEMS WITH MY LAST FILE — fix every one of these:');
  for (const it of issues) lines.push(`  - ${it.label}: ${it.error}`);
  lines.push('');
  lines.push(
    `This file is for cell "${cell}", language "${language}". ` +
      'Give every persona a unique id. Output ONLY the JSON, no commentary.',
  );
  return lines.join('\n');
}

// ── Section 4: Shared judge checklists per call type ────────────────

function SharedChecklistSection({ cat }: { cat: Catalogue }) {
  const cells = useMemo(() => cat.cells.filter((c) => c.enabled), [cat.cells]);
  const [cell, setCell] = useState(cells[0]?.slug ?? '');
  const [agentType, setAgentType] = useState('initial');
  const [lang, setLang] = useState<Language>('EN');
  const [text, setText] = useState('');

  const typesQ = useQuery({ queryKey: ['agent-types'], queryFn: api.listAgentTypes });
  const currentQ = useQuery({
    queryKey: ['shared-watch-for', cell],
    queryFn: () => api.getSharedWatchFor(cell),
    enabled: !!cell,
  });

  // Unsaved edits survive dropdown flips: drafts are kept per
  // (cell, call type, language) and restored when you flip back.
  const draftsRef = useRef<Record<string, string>>({});
  const draftKey = `${cell}|${agentType}|${lang}`;

  // Load the draft (if any) or the saved rows into the textarea
  // whenever the selection or the fetched data changes.
  useEffect(() => {
    const draft = draftsRef.current[draftKey];
    if (draft !== undefined) {
      setText(draft);
      return;
    }
    const rows = currentQ.data?.[agentType]?.[lang] ?? [];
    setText(rows.join('\n'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQ.data, draftKey]);

  const onTextChange = (value: string) => {
    setText(value);
    draftsRef.current[draftKey] = value;
  };

  const qc = useQueryClient();
  const saveMut = useMutation({
    mutationFn: () => {
      const rows = text.split('\n').map((r) => r.trim()).filter(Boolean);
      if (rows.length > 40) {
        throw new Error(`Too many rules (${rows.length}) — max 40. Prune the list first.`);
      }
      return api.putSharedWatchFor(cell, agentType, lang, rows);
    },
    onSuccess: (r) => {
      toast.success(
        r.rows.length
          ? `Saved ${r.rows.length} shared rule(s) — the judge applies them from the next run`
          : 'Cleared the shared rules for this call type',
      );
      delete draftsRef.current[draftKey];
      qc.invalidateQueries({ queryKey: ['shared-watch-for', cell] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const genMut = useMutation({
    // Snapshot the selection at click time so a multi-second LLM call
    // can never land its suggestions under a different cell/type/lang
    // the admin flipped to mid-flight.
    mutationFn: (key: { cell: string; agentType: string; lang: Language }) =>
      api.generateSharedWatchFor(key.cell, key.agentType, key.lang),
    onSuccess: (r, key) => {
      if (!r.suggested_rows.length) {
        toast.info('The model suggested nothing — is the conversation design uploaded?');
        return;
      }
      const targetKey = `${key.cell}|${key.agentType}|${key.lang}`;
      const base = (draftsRef.current[targetKey] ??
        (targetKey === draftKey ? text : (currentQ.data?.[key.agentType]?.[key.lang] ?? []).join('\n'))
      ).trim();
      const next = [base, ...r.suggested_rows].filter(Boolean).join('\n');
      draftsRef.current[targetKey] = next;
      if (targetKey === draftKey) setText(next);
      toast.success(
        `Suggested ${r.suggested_rows.length} rule(s) for ${key.agentType}/${key.lang} from the conversation design — review, edit, then Save`,
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const rowCount = text.split('\n').filter((r) => r.trim()).length;

  return (
    <section>
      <h2 className="flex items-center gap-2 text-base font-medium text-muted-foreground">
        <ListChecks className="size-4" /> 4 · Shared judge rules per call type
      </h2>
      <p className="mt-1 text-xs text-muted-foreground">
        One rule per line. The judge appends these AFTER each persona's own watch_for for every
        conversation of this call type in this cell — so always-on rules (ask caps, consequence
        sequence, turn endings) are graded on every call without pasting them into every persona.
        Saving replaces the list; the previous version is archived.
      </p>

      <div className={cn('mt-3 space-y-3', cardCls)}>
        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <label className={labelCls}>Cell</label>
            <select value={cell} onChange={(e) => setCell(e.target.value)} className={inputCls}>
              {cells.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.bank} · {c.product} · {c.bucket}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Call type</label>
            <select
              value={agentType}
              onChange={(e) => setAgentType(e.target.value)}
              className={inputCls}
            >
              {(typesQ.data ?? []).map((t) => (
                <option key={t.slug} value={t.slug}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Language</label>
            <select
              value={lang}
              onChange={(e) => setLang(e.target.value as Language)}
              className={inputCls}
            >
              <option value="EN">EN</option>
              <option value="AR">AR</option>
            </select>
          </div>
        </div>

        <textarea
          value={text}
          onChange={(e) => onTextChange(e.target.value)}
          rows={8}
          placeholder={
            'Asks for payment at most 2 times, then moves on (cap from the design).\n' +
            'Delivers the consequence sequence as ONE statement with all items in order.\n' +
            'Every active turn ends with a question, choice, confirmation or permission ask.'
          }
          className="w-full rounded-md border border-input bg-card p-3 font-mono text-xs leading-relaxed shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-xs text-muted-foreground">
            {rowCount} rule{rowCount === 1 ? '' : 's'} · graded as extra rows on every{' '}
            {agentType} run in {cell}/{lang}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => genMut.mutate({ cell, agentType, lang })}
              disabled={genMut.isPending || !cell}
              title="Ask the model to draft rules from this cell's conversation design. Nothing is saved until you review and click Save."
              className="inline-flex h-9 items-center gap-1.5 rounded-md border border-primary/40 bg-card px-3 text-sm font-medium text-primary shadow-sm hover:bg-accent disabled:opacity-60"
            >
              {genMut.isPending ? <Spinner className="size-4" /> : <Sparkles className="size-4" />}
              {genMut.isPending ? 'Drafting…' : 'Suggest from design'}
            </button>
            <button
              type="button"
              onClick={() => saveMut.mutate()}
              disabled={saveMut.isPending || !cell}
              className={btnCls}
            >
              {saveMut.isPending ? <Spinner className="size-4 text-primary-foreground" /> : null}
              Save
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
